import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, filter, map, take } from 'rxjs';
import { PermissionsService } from '../auth/permissions.service';
import { MyTeamsStore } from '../../features/my-team/services/my-teams-store.service';
import { roleAtLeast } from '../../features/my-team/models/my-team.model';

/**
 * Team-scoped editor guard. Allows a global manager (admin, editor or stats_editor role) or
 * a user whose role on the route's `:teamId` is coach or higher. Otherwise redirects into the
 * team workspace (or the app root if the route carries no valid teamId).
 *
 * Team memberships load via an async httpResource; on a cold/direct navigation it may not
 * have resolved yet, so the coach check waits for the resource to finish loading before
 * deciding — otherwise a legitimate coach would be bounced by an empty membership list.
 */
export const teamCoachGuard: CanActivateFn = (route): boolean | UrlTree | Observable<boolean | UrlTree> => {
  const permissions = inject(PermissionsService);
  const myTeams = inject(MyTeamsStore);
  const router = inject(Router);

  const teamId = Number(route.paramMap.get('teamId'));
  if (!Number.isFinite(teamId) || teamId <= 0) {
    return router.createUrlTree(['/']);
  }

  if (permissions.canEdit()) {
    return true;
  }

  return toObservable(myTeams.teamsResource.isLoading).pipe(
    filter((loading) => !loading),
    take(1),
    map(() =>
      roleAtLeast(myTeams.roleFor(teamId), 'coach') ? true : router.createUrlTree(['/my-teams', teamId]),
    ),
  );
};
